import { useState } from "react";
import { Home, FileText, MessageSquare, BarChart3, Settings } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useHapticButton } from "@/hooks/useHapticButton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useFeedbackBadges } from "@/hooks/useFeedbackBadges";

interface NavItem {
  icon: typeof Home;
  label: string;
  path: string;
  badgeKey?: "feedback" | "pages";
}

export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const triggerHaptic = useHapticButton('selection');
  const { user } = useAuth();
  const [activeAnimation, setActiveAnimation] = useState<string | null>(null);

  const { getBadgeCount } = useFeedbackBadges();

  const navItems: NavItem[] = [
    { icon: Home, label: "Home", path: "/dashboard" },
    { icon: FileText, label: "Pages", path: "/dashboard/pages", badgeKey: "pages" },
    { icon: MessageSquare, label: "Feedback", path: "/dashboard/feedback", badgeKey: "feedback" },
    { icon: BarChart3, label: "Analytics", path: "/dashboard/analytics" },
    { icon: Settings, label: "Settings", path: "/dashboard/settings" },
  ];

  const handleNavClick = async (path: string, badgeKey?: "feedback" | "pages") => {
    triggerHaptic();
    
    // Trigger tap animation
    setActiveAnimation(path);
    setTimeout(() => setActiveAnimation(null), 200);
    
    // Update viewed timestamps
    if (badgeKey === "feedback" && user?.id) {
      await supabase
        .from("profiles")
        .update({ last_feedback_viewed_at: new Date().toISOString() })
        .eq("id", user.id);
    } else if (badgeKey === "pages" && user?.id) {
      await supabase
        .from("profiles")
        .update({ last_pages_viewed_at: new Date().toISOString() })
        .eq("id", user.id);
    }
    
    navigate(path);
  };

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 bg-sidebar border-t border-border/40 md:hidden"
      data-tour="mobile-navigation"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const badgeCount = getBadgeCount(item.badgeKey);
          
          return (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path, item.badgeKey)}
              data-tour={item.path === "/dashboard/pages" ? "mobile-pages-nav" : undefined}
              className={cn(
                "flex flex-col items-center justify-center gap-1 flex-1 h-full relative",
                "transition-all duration-150",
                active ? "text-primary" : "text-muted-foreground",
                activeAnimation === item.path && "scale-90"
              )}
            >
              <div className={cn(
                "relative transition-transform duration-200",
                activeAnimation === item.path && "scale-110"
              )}>
                <Icon 
                  className={cn(
                    "h-5 w-5 transition-all duration-200",
                    active && "animate-scale-in"
                  )} 
                  strokeWidth={1.5} 
                />
                {badgeCount && badgeCount > 0 && (
                  <span className={cn(
                    "absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 text-[10px] font-semibold",
                    "bg-destructive text-destructive-foreground rounded-full",
                    "flex items-center justify-center",
                    "animate-scale-in"
                  )}>
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </span>
                )}
              </div>
              <span className={cn(
                "text-[10px] font-medium transition-all duration-150",
                active && "font-semibold text-primary"
              )}>
                {item.label}
              </span>
              {active && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-primary rounded-full transition-all duration-200" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
